import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import RoutinesEntryPoint from '@databox/ember-integrations/lib/components/Ai/Routines/RoutinesEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { encodeAiChatPrompt } from 'webapp/utils/ai-chat-payload';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

// Sent on the user's behalf, unlike the composer pill's prompt, which is only prefilled.
const CREATE_WITH_GENIE_PROMPT = 'Guide me through creating a routine';

interface GenieRoutinesSignature {
	Element: HTMLDivElement;
}

export default class GenieRoutinesComponent extends Component<GenieRoutinesSignature> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderRoutines());
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	openRoutine(routine: { id: string }) {
		this.navigationService.transitionToRoute('routines.detail', routine.id);
	}

	@action
	openRun(run: { id: string; routineId: string }) {
		// Opened thread or not: `Run now` answers with a queued run whose thread the worker has not
		// created yet, and the run page owns what it shows until it has one.
		this.navigationService.transitionToRoute('routines.run', run.routineId, run.id);
	}

	@action
	createWithGenie() {
		this.navigationService.transitionToRoute('genie', {
			queryParams: { chat: encodeAiChatPrompt(CREATE_WITH_GENIE_PROMPT) },
		});
	}

	private renderRoutines() {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken,
			dataToken,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			trackingOrigin: this.aiChatService.trackingOrigin,
			showToast: this.aiChatService.showToast,
			onSelectRoutine: this.openRoutine,
			onSelectRun: this.openRun,
			onCreateWithGenie: this.createWithGenie,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			slack: this.aiChatService.slackHostProps,
		};

		this.reactService.render(
			this.reactElement,
			RoutinesEntryPoint(props),
			getShadowDomMode(this.accountService?.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="genie-routines-container"
			...attributes
		/>
	</template>
}
